import { Fragment, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { IconButton } from '@/components/ui/Button';
import { useNavigation } from '@/hooks/useNavigation';
import type { NavItem } from '@/types';
import { navIcon } from './navIcons';

function containsRoute(item: NavItem, pathname: string): boolean {
  if (item.path && (pathname === item.path || pathname.startsWith(`${item.path}/`))) return true;
  return item.children.some((child) => containsRoute(child, pathname));
}

export function Sidebar({
  open,
  onClose,
  collapsed,
}: {
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
}) {
  const { data, isPending, isError } = useNavigation();
  const items = data?.sidebar ?? [];

  return (
    <>
      {/* Mobile overlay */}
      <div
        onClick={onClose}
        className={cn(
          'fixed inset-0 z-30 bg-navy-950/50 transition-opacity lg:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        aria-hidden
      />

      {/* Outer sidebar - dark navy background */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex flex-col bg-[#071538] text-white transition-[transform,width] duration-200',
          'lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
          collapsed ? 'w-[76px]' : 'w-[268px]',
        )}
      >
        {/* Inner wrapper - navy matching 2nd image, rounded bottom corners, wraps content height */}
        <div
          className={cn(
            'flex flex-col overflow-hidden bg-[#03153B]',
            collapsed ? 'rounded-b-[26px]' : 'rounded-b-[26px]',
          )}
        >
          {/* Logo section */}
          <div
            className={cn(
              'relative flex h-[170px] items-center justify-center bg-[#09245f]',
              collapsed ? 'px-2' : 'px-5',
            )}
          >
            <img
              src="/asset/image/logo.png"
              alt="BD Design Pvt. Ltd."
              className={cn(
                'object-contain brightness-0 invert transition-all duration-300',
                collapsed ? 'h-10 w-10' : 'h-38 w-auto',
              )}
            />

            {!collapsed && (
              <IconButton
                label="Close menu"
                onClick={onClose}
                className="absolute right-5 text-white/70 hover:bg-white/10 hover:text-white lg:hidden"
                size="sm"
              >
                <X className="size-4" aria-hidden />
              </IconButton>
            )}
          </div>

          {/* Navigation menu */}
          <nav
            className="max-h-[calc(100vh-200px)] overflow-y-auto bg-[#09245f] px-3 pb-4"
            aria-label="Main navigation"
          >
            {isPending && <NavSkeleton collapsed={collapsed} />}

            {isError && !collapsed && (
              <p className="px-3 py-2 text-[11px] leading-relaxed text-white/45">
                The menu could not be loaded. Refresh the page, or sign in again if the
                problem continues.
              </p>
            )}

            <ul className="flex flex-col gap-1">
              {items.map((item, index) => (
                <Fragment key={item.code}>
                  {!collapsed && item.section && item.section !== items[index - 1]?.section && (
                    <li className="px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-wider text-white/35">
                      {item.section}
                    </li>
                  )}
                  <li>
                    <NavRow item={item} collapsed={collapsed} onNavigate={onClose} />
                  </li>
                </Fragment>
              ))}
            </ul>
          </nav>
        </div>
      </aside>
    </>
  );
}

function NavRow({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate: () => void;
}) {
  const Icon = navIcon(item.icon, item.code);
  const location = useLocation();
  const active = containsRoute(item, location.pathname);

  const [openOverride, setOpenOverride] = useState<boolean | null>(null);
  const open = openOverride ?? active;

  const activeStyles =
    'bg-[#1C7CFF] text-white shadow-[0_4px_14px_-4px_rgba(28,124,255,0.5)]';
  const inactiveStyles = 'text-white/70 hover:bg-white/8 hover:text-white';

  // Parent with children but no path (collapsible)
  if (item.children.length > 0 && !item.path) {
    if (collapsed) {
      const firstChild = item.children[0];
      if (!firstChild?.path) return null;
      return (
        <Link
          to={firstChild.path}
          title={item.label}
          className={cn(
            'flex items-center justify-center rounded-xl px-0 py-2.5 transition-colors',
            active ? activeStyles : inactiveStyles,
          )}
        >
          <Icon className="size-5 shrink-0" aria-hidden />
        </Link>
      );
    }

    return (
      <div>
        <button
          type="button"
          onClick={() => setOpenOverride(!open)}
          aria-expanded={open}
          className={cn(
            'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors',
            active ? activeStyles : inactiveStyles,
          )}
        >
          <Icon className="size-5 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
          <ChevronDown
            className={cn('size-4 shrink-0 transition-transform', open && 'rotate-180')}
            aria-hidden
          />
        </button>

        {open && (
          <ul className="animate-rise relative mt-1 ml-[18px] flex flex-col gap-0.5 border-l border-white/15 pl-4">
            {item.children.map((child) => {
              const childActive = containsRoute(child, location.pathname);
              return (
                <li key={child.code}>
                  <NavLink
                    to={child.path ?? '#'}
                    end={child.exact}
                    onClick={onNavigate}
                    className={cn(
                      'flex items-center gap-2.5 rounded-lg py-2 pr-3 pl-1 text-[13px] transition-colors',
                      childActive ? 'font-bold text-white' : 'font-medium text-white/60 hover:text-white',
                    )}
                  >
                    <span
                      className={cn(
                        'inline-flex size-[7px] shrink-0 rounded-full border transition-colors',
                        childActive ? 'border-[#1C7CFF] bg-[#1C7CFF]' : 'border-white/40 bg-transparent',
                      )}
                      aria-hidden
                    />
                    <span className="truncate">{child.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  if (!item.path) return null;

  // External link
  if (item.external) {
    return (
      <a
        href={item.path}
        target="_blank"
        rel="noreferrer"
        title={collapsed ? item.label : undefined}
        className={cn(
          'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-white/70 transition-colors hover:bg-white/8 hover:text-white',
          collapsed && 'justify-center px-0',
        )}
      >
        <Icon className="size-5 shrink-0" aria-hidden />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </a>
    );
  }

  // Regular internal link
  return (
    <NavLink
      to={item.path}
      end={item.exact}
      onClick={onNavigate}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors',
          collapsed && 'justify-center px-0',
          isActive ? activeStyles : inactiveStyles,
        )
      }
    >
      <Icon className="size-5 shrink-0" aria-hidden />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
}

/** Placeholder rows while the menu loads, so the rail does not jump. */
function NavSkeleton({ collapsed }: { collapsed: boolean }) {
  return (
    <ul className="flex flex-col gap-1" aria-hidden>
      {Array.from({ length: 5 }).map((_, index) => (
        <li
          key={index}
          className={cn(
            'skeleton-dark h-[42px] rounded-xl',
            collapsed ? 'mx-auto w-[42px]' : 'w-full',
          )}
        />
      ))}
    </ul>
  );
}